import React, { useState, useRef, useMemo } from 'react';
import { Heart } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import VariantSheetDesktop from '../VariantSheetDesktop/VariantSheetDesktop';
import VariantOptionsModal from '../VariantOptionModal/VariantOptionsModal';
import './styles/ProductCardsD.css';

interface CategoryCard {
  id: number;
  name: string;
  image: string;
  alt: string;
}

interface ProductCard {
  id: number;
  name: string;
  image: string;
  alt: string;
  slug: string;
  price?: string;
  _id?: string;
  productData?: any;
  quantity?: number;
}

interface ProductCardsDProps {
  categoryCards: CategoryCard[];
  productCards: ProductCard[];
  heading: string;
  onCategoryClick?: (category: string) => void;
  loading?: boolean;
  error?: string | null;
  onAddToCart?: (product: any) => void;
  onWishlistToggle?: (product: any) => void;
  onMoreInfo?: (product: any) => void;
  onViewOptions?: (product: any) => void;
  isItemInWishlist?: (id: string) => boolean;
  products?: any[];
}

const convertVariantsForDisplay = (product: any) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.name || product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  return product.variants.map((variant: any) => {
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    return baseVariant;
  });
};

const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
  if (!variants || variants.length === 0) return undefined;
  
  const firstVariant = variants[0];
  if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
    return undefined;
  }
  
  if (firstVariant.variantLevels) {
    return Object.keys(firstVariant.variantLevels).map((key: string) => {
      const match = key.match(/level(\d+)/i);
      if (match) {
        return firstVariant.variantLevels[key];
      }
      return firstVariant.variantLevels[key];
    });
  }
  
  const attributeKeys = Object.keys(firstVariant.attributes);
  if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
    return attributeKeys;
  }
  
  return undefined;
};

const ProductCardsD: React.FC<ProductCardsDProps> = ({ 
  categoryCards, 
  productCards, 
  heading, 
  onCategoryClick, 
  loading, 
  error,
  onAddToCart,
  onWishlistToggle,
  onMoreInfo,
  isItemInWishlist}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState(
    categoryCards && categoryCards.length > 0 ? categoryCards[0].name : ''
  );
  const [modalProduct, setModalProduct] = useState<any>(null);

  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: modalProduct.variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const handleAddToCartClick = (productCard: ProductCard, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    
    if (onAddToCart && productCard.productData) {
      onAddToCart(productCard.productData);
    }
  };

  const handleCartQuantityChange = (cartItem: any, newQuantity: number) => {
    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleWishlistClick = (productCard: ProductCard, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    
    if (onWishlistToggle && productCard.productData) {
      onWishlistToggle(productCard.productData);
    }
  };

  const handleCardClick = (productCard: ProductCard, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    
    if (onMoreInfo && productCard.productData) {
      onMoreInfo(productCard.productData);
    }
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    if (onCategoryClick) {
      onCategoryClick(categoryName);
    }
  };

  return (
    <div className="product-cards-d">
      <div className="product-cards-d__left">
        <div className="product-cards-d__categories">
          {categoryCards.map((card) => (
            <div 
              key={card.id} 
              className={`product-cards-d__category-card ${selectedCategory === card.name ? 'product-cards-d__category-card--selected' : ''}`} 
              onClick={() => handleCategoryClick(card.name)} 
              style={{ cursor: 'pointer' }}
            >
              <div className="product-cards-d__category-image-container">
                <img 
                  src={card.image} 
                  alt={card.alt}
                  className="product-cards-d__category-image"
                  draggable="false"
                />
              </div>
              <h3 className="product-cards-d__category-name">{card.name}</h3>
            </div>
          ))}
        </div>
      </div>
      
      <div className="product-cards-d__right">
        <h2 className="product-cards-d__heading">{heading}</h2>
        
        {loading && (
          <div className="loading-container" style={{ textAlign: 'center', padding: '20px' }}>
            <p>Loading products...</p>
          </div>
        )}
        
        {error && (
          <div className="error-container" style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
            <p>{error}</p>
          </div>
        )}
        
        {!loading && !error && productCards.length === 0 && (
          <div className="no-products-container" style={{ textAlign: 'center', padding: '20px' }}>
            <p>No products found for this category.</p>
          </div>
        )}
        
        {!loading && productCards.length > 0 && (
          <div 
            className="product-cards-d__products-row"
            ref={scrollContainerRef}
          >
            {productCards.map((card) => {
              const isWishlisted = isItemInWishlist && card._id ? isItemInWishlist(card._id) : false;
              const cartItem = cartItems && cartItems.find(item => {
                if (item.productId !== (card._id || String(card.id))) return false;
                return !item.variantSku || item.variantSku === '';
              });
              const isInCart = !!cartItem;
              const cartQuantity = cartItem?.quantity || 0;
              // Assume card.quantity is available, fallback to 1 if not
              const quantity = typeof card.quantity === 'number' ? card.quantity : 1;
              return (
                <div 
                  key={card.id} 
                  className="product-cards-d__product-card"
                  onClick={(e) => handleCardClick(card, e)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  {/* Out of Stock Badge */}
                  {quantity === 0 && (
                    <div style={{position: 'absolute', top: 12, left: 12, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
                      Out of Stock
                    </div>
                  )}
                  {/* Wishlist Icon */}
                  <div 
                    className="product-cards-d__wishlist-icon" 
                    title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    onClick={(e) => handleWishlistClick(card, e)}
                    onTouchEnd={(e) => handleWishlistClick(card, e as any)}
                    role="button"
                    aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      zIndex: 10,
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '50%',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s ease'
                    }}
                  >
                    <Heart 
                      size={20} 
                      color={isWishlisted ? '#e0245e' : '#000000'} 
                      strokeWidth={2.2} 
                      fill={isWishlisted ? '#e0245e' : 'none'} 
                    />
                  </div>

                  <div className="product-cards-d__product-image-container">
                    <img 
                      src={card.image} 
                      alt={card.alt}
                      className="product-cards-d__product-image"
                      draggable="false"
                    />
                  </div>
                  <div className="product-cards-d__product-content">
                    <h4 className="product-cards-d__product-name">{card.name}</h4>
                    <div className="product-cards-d__price-and-button">
                      {card.price && (!card.productData?.variants || card.productData.variants.length === 0) && (
                        <p className="product-cards-d__product-price">
                          {(() => {
                            const match = typeof card.price === 'string' ? card.price.match(/([\d,.]+)/) : null;
                            if (match) {
                              const num = parseFloat(match[1].replace(/,/g, ''));
                              if (!isNaN(num)) {
                                return card.price.replace(match[1], Math.round(num).toString());
                              }
                            }
                            return card.price;
                          })()}
                        </p>
                      )}
                      {isInCart ? (
                        <div className="cart-quantity-controls">
                          <button
                            type="button"
                            className="cart-quantity-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCartQuantityChange(cartItem, cartQuantity - 1);
                            }}
                            style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                            aria-label="Decrease quantity"
                          >
                            -
                          </button>
                          <span className="cart-quantity-display">{cartQuantity}</span>
                          <button
                            type="button"
                            className="cart-quantity-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                                handleCartQuantityChange(cartItem, cartQuantity + 1);
                              }
                            }}
                            style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                            aria-label="Increase quantity"
                            disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
                          >
                            +
                          </button>
                        </div>
                      ) : card.productData?.variants && card.productData.variants.length > 0 ? (
                        <button
                          type="button"
                          className="product-cards-d__view-options"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalProduct(card.productData);
                          }}
                        >
                          View Options
                        </button>
                      ) : (
                        <button 
                          type="button"
                          className="product-cards-d__add-cart"
                          onClick={(e) => handleAddToCartClick(card, e)}
                          onTouchEnd={(e) => handleAddToCartClick(card, e as any)}
                          style={quantity === 0 ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed' } : {}}
                          disabled={quantity === 0}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Variant Options Modal */}
      {modalProduct && modalData && (
        <VariantOptionsModal
          isOpen={!!modalProduct}
          onClose={() => setModalProduct(null)}
          title={modalProduct ? (modalProduct.name || 'Product Options') : ''}
        >
          <VariantSheetDesktop
            product={modalProduct}
            variants={modalData.variants}
            hierarchicalVariants={modalData.hierarchicalVariants}
            variantHierarchy={modalData.variantHierarchy}
            onVariantSelect={() => {}}
          />
        </VariantOptionsModal>
      )}
    </div>
  );
};

export default ProductCardsD;